(() => {
  const STORAGE_KEY = 'by-gym-cart-discount';
  const WISHLIST_KEY = 'm-wishlist-products';
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const readStorage = (key, fallback = '') => {
    try {
      const value = localStorage.getItem(key);
      return value === null ? fallback : value;
    } catch (_error) {
      return fallback;
    }
  };

  const writeStorage = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (_error) {}
  };

  const activeCurrency = (fallback = 'CAD') => {
    const shopifyCurrency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    const currencyMeta = document.querySelector('meta[property="og:price:currency"]');
    return String(shopifyCurrency || (currencyMeta && currencyMeta.content) || fallback).toUpperCase();
  };

  const formatMoney = (cents, fallbackCurrency = 'CAD') => {
    const amount = Number(cents || 0) / 100;
    const currency = activeCurrency(fallbackCurrency);
    const locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch (_error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const shippingThreshold = (progress) => {
    const baseCents = Number(progress.dataset.byThresholdBaseCents);
    const baseCurrency = progress.dataset.byThresholdBaseCurrency;
    const currency = activeCurrency(baseCurrency);

    if (!Number.isFinite(baseCents) || baseCents <= 0 || !baseCurrency || !currency) return 0;
    if (baseCurrency === currency) return baseCents;
    if (baseCurrency !== 'CAD') return 0;

    const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    if (!Number.isFinite(rate) || rate <= 0) return 0;
    return Math.ceil((baseCents * rate) - 0.000001);
  };

  const syncShippingProgress = (scope = document) => {
    scope.querySelectorAll('[data-by-cart-progress]').forEach((progress) => {
      const eligibleCount = Number(progress.dataset.byEligibleCount) || 0;
      const signatureCount = Number(progress.dataset.bySignatureCount) || 0;
      const message = progress.querySelector('[data-by-progress-message]');
      const track = progress.querySelector('.by-gym-cart__progress-track');
      const signatureNote = progress.querySelector('[data-by-signature-shipping-note]');
      const signatureOnly = signatureCount > 0 && eligibleCount === 0;
      const check = progress.querySelector('[data-by-progress-check]');

      progress.classList.remove('is-unlocked');
      if (check) check.setAttribute('hidden', '');

      if (track) track.hidden = eligibleCount === 0;
      if (signatureNote) signatureNote.hidden = signatureCount === 0 || eligibleCount === 0;

      if (signatureOnly) {
        progress.hidden = false;
        if (message && message.textContent !== progress.dataset.bySignatureOnlyMessage) {
          message.textContent = progress.dataset.bySignatureOnlyMessage;
        }
        return;
      }

      const threshold = shippingThreshold(progress);
      if (!threshold || eligibleCount === 0) {
        progress.hidden = true;
        return;
      }

      const subtotal = Math.max(0, Number(progress.dataset.bySubtotalCents) || 0);
      const remaining = Math.max(0, threshold - subtotal);
      const percentage = Math.min(100, Math.max(0, (subtotal / threshold) * 100));
      const fill = progress.querySelector('.by-gym-cart__progress-track span');
      const lockedTemplate = signatureCount > 0 ? progress.dataset.byMixedLockedTemplate : progress.dataset.byLockedTemplate;
      const unlockedMessage = signatureCount > 0 ? progress.dataset.byMixedUnlockedMessage : progress.dataset.byUnlockedMessage;

      progress.hidden = false;
      progress.classList.toggle('is-unlocked', remaining === 0);
      if (check) check.toggleAttribute('hidden', remaining !== 0);
      if (message) {
        const nextMessage = remaining === 0
          ? (unlockedMessage || '').replace(/^[✓✔]\s*/, '')
          : (lockedTemplate || '').replace('[amount]', formatMoney(remaining, progress.dataset.byThresholdBaseCurrency));
        if (message.textContent !== nextMessage) message.textContent = nextMessage;
      }
      if (fill) {
        requestAnimationFrame(() => {
          fill.style.width = `${percentage.toFixed(2)}%`;
        });
      }
    });
  };

  const productImage = (product) => {
    let image = product.featured_image || (product.images && product.images[0]) || '';
    if (image && typeof image === 'object') image = image.src || image.url || '';
    if (!image) return '';
    try {
      const url = new URL(image, window.location.origin);
      url.searchParams.set('width', '180');
      return url.toString();
    } catch (_error) {
      return image;
    }
  };

  const availableVariants = (product) => (product.variants || []).filter((variant) => variant.available !== false);

  const renderUpsellCard = (product, labels) => {
    const variants = availableVariants(product);
    if (!variants.length) return '';

    const firstVariant = variants[0];
    const price = Number(product.price_min ?? product.price ?? firstVariant.price ?? 0);
    const compareAt = Number(product.compare_at_price_max ?? product.compare_at_price ?? firstVariant.compare_at_price ?? 0);
    const sale = compareAt > price;
    const badge = sale ? `SALE | SAVE ${formatMoney(compareAt - price)}` : 'NEW';
    const image = productImage(product);
    const url = product.url || `${root()}products/${product.handle}`;
    const hasChoices = variants.length > 1 || (firstVariant.title && firstVariant.title !== 'Default Title');
    const options = variants.map((variant) => {
      const title = variant.public_title || variant.title || labels.choose;
      return `<option value="${escapeHtml(variant.id)}">${escapeHtml(title)}</option>`;
    }).join('');
    const selector = hasChoices
      ? `<select class="by-gym-cart__upsell-select" aria-label="${escapeHtml(labels.choose)}">${options}</select>`
      : `<input type="hidden" class="by-gym-cart__upsell-select" value="${escapeHtml(firstVariant.id)}">`;

    return `
      <article class="by-gym-cart__upsell-card" data-by-upsell-card>
        <a class="by-gym-cart__upsell-image" href="${escapeHtml(url)}" tabindex="-1" aria-hidden="true">
          ${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy" width="90" height="118">` : ''}
        </a>
        <div class="by-gym-cart__upsell-content">
          <span class="by-gym-cart__upsell-badge">${escapeHtml(badge)}</span>
          <a class="by-gym-cart__upsell-title" href="${escapeHtml(url)}">${escapeHtml(product.title)}</a>
          <div class="by-gym-cart__upsell-price">
            <span>${escapeHtml(formatMoney(price))}</span>
            ${sale ? `<del>${escapeHtml(formatMoney(compareAt))}</del>` : ''}
          </div>
          <div class="by-gym-cart__upsell-actions">
            ${selector}
            <button type="button" class="by-gym-cart__upsell-add" data-by-upsell-add>${escapeHtml(labels.add)}</button>
          </div>
        </div>
      </article>`;
  };

  const loadRecommendations = async (container) => {
    if (!container || container.dataset.byLoaded === 'true') return;
    container.dataset.byLoaded = 'true';

    const productId = container.dataset.productId;
    const list = container.querySelector('[data-by-upsell-list]');
    if (!productId || !list) {
      container.remove();
      return;
    }

    const labels = {
      add: container.dataset.addLabel || 'Add',
      adding: container.dataset.addingLabel || 'Adding',
      added: container.dataset.addedLabel || 'Added',
      choose: container.dataset.chooseLabel || 'Choose an option'
    };
    const cartIds = new Set((container.dataset.cartProductIds || '').split(',').map(Number).filter(Boolean));

    try {
      const endpoint = `${root()}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=8&intent=related`;
      const response = await fetch(endpoint, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const data = await response.json();
      const products = (data.products || [])
        .filter((product) => product.available !== false && !cartIds.has(Number(product.id)) && availableVariants(product).length)
        .slice(0, 3);

      if (!products.length) {
        container.remove();
        return;
      }

      list.innerHTML = products.map((product) => renderUpsellCard(product, labels)).join('');
      container._byLabels = labels;
    } catch (_error) {
      container.remove();
    }
  };

  const syncWishlistButtons = (scope = document) => {
    let saved = [];
    try {
      saved = JSON.parse(readStorage(WISHLIST_KEY, '[]')) || [];
    } catch (_error) {}
    const handles = new Set(saved);
    scope.querySelectorAll('[data-by-cart-item-wishlist]').forEach((button) => {
      button.classList.toggle('added-to-wishlist', handles.has(button.dataset.productHandle));
    });
  };

  const restoreDiscount = (scope = document) => {
    const input = scope.querySelector('[data-by-discount-code]');
    const stored = readStorage(STORAGE_KEY, '');
    if (input && stored && !input.value) input.value = stored;
  };

  const hydrate = (scope = document) => {
    syncShippingProgress(scope);
    scope.querySelectorAll('[data-by-cart-upsells]').forEach(loadRecommendations);
    syncWishlistButtons(scope);
    restoreDiscount(scope);
  };

  const applyDiscount = async (button) => {
    const drawer = button.closest('.by-gym-cart');
    const input = drawer && drawer.querySelector('[data-by-discount-code]');
    const message = drawer && drawer.querySelector('[data-by-discount-message]');
    const code = input ? input.value.trim() : '';

    if (!code) {
      if (message) {
        message.textContent = button.dataset.error || 'Enter a discount code.';
        message.classList.add('is-error');
      }
      input && input.focus();
      return;
    }

    writeStorage(STORAGE_KEY, code);
    button.disabled = true;
    if (message) {
      message.textContent = '';
      message.classList.remove('is-error');
    }

    try {
      const redirect = `${root()}cart`;
      await fetch(`${root()}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (message) message.textContent = button.dataset.success || 'Code added for checkout.';
    } catch (_error) {
      if (message) message.textContent = button.dataset.success || 'Code added for checkout.';
    } finally {
      button.disabled = false;
    }
  };

  const addUpsell = async (button) => {
    const card = button.closest('[data-by-upsell-card]');
    const container = button.closest('[data-by-cart-upsells]');
    const variantInput = card && card.querySelector('.by-gym-cart__upsell-select');
    const variantId = variantInput && variantInput.value;
    const labels = (container && container._byLabels) || {
      add: container?.dataset.addLabel || 'Add',
      adding: container?.dataset.addingLabel || 'Adding',
      added: container?.dataset.addedLabel || 'Added'
    };
    if (!variantId) return;

    button.disabled = true;
    button.textContent = labels.adding;

    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Unable to add item');

      button.textContent = labels.added;
      const drawer = document.querySelector('m-cart-drawer');
      if (drawer && typeof drawer.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate(true);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'by-cart-upsell' } }));
    } catch (error) {
      button.textContent = error && error.message ? error.message : labels.add;
      button.title = button.textContent;
      window.setTimeout(() => {
        button.textContent = labels.add;
        button.disabled = false;
      }, 1800);
      return;
    }

    window.setTimeout(() => {
      button.disabled = false;
      button.textContent = labels.add;
    }, 1200);
  };

  document.addEventListener('click', (event) => {
    const discountButton = event.target.closest('[data-by-discount-apply]');
    if (discountButton) {
      event.preventDefault();
      applyDiscount(discountButton);
      return;
    }

    const upsellButton = event.target.closest('[data-by-upsell-add]');
    if (upsellButton) {
      event.preventDefault();
      addUpsell(upsellButton);
    }
  });

  document.addEventListener('click', (event) => {
    const minus = event.target.closest('[data-by-cart-item] .m-quantity__button[name="minus"]');
    if (!minus) return;
    const item = minus.closest('[data-by-cart-item]');
    const input = item && item.querySelector('.m-quantity__input');
    const current = Number(input && input.value);
    const minimum = Number((input && input.min) || 1);
    if (current > minimum) return;

    const remove = item.querySelector('m-cart-remove-button');
    if (!remove) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    remove.click();
  }, true);

  document.addEventListener('submit', (event) => {
    if (!event.target.matches('#cart-drawer-form')) return;
    const input = document.querySelector('[data-by-discount-code]');
    if (input && input.value.trim()) writeStorage(STORAGE_KEY, input.value.trim());
  }, true);

  const initialize = () => {
    hydrate(document);
    document.querySelectorAll('m-cart-drawer, m-cart').forEach((cart) => {
      const observer = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => mutation.addedNodes.length || mutation.removedNodes.length)) hydrate(cart);
      });
      observer.observe(cart, { childList: true, subtree: true });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();

